import type { Ohaeng } from '../types/index';
import type { FourPillars } from '../types/index';
import type { YongSin, YongSinResult, DayMasterStrength, YongSinMethod, StrengthLevel, YongshinPriority } from '../types/index';
import { getStemElement, getElementRelation } from '../core/five-elements';
import { determineJohuYongSin } from './yong-sin-johu';
import { determineTonggwanYongSin } from './yong-sin-tonggwan';
import { determineByeongyakYongSin } from './yong-sin-byeongyak';

export const ALL_ELEMENTS: Ohaeng[] = ['木', '火', '土', '金', '水'];

/**
 * 용신/희신/기신/구신 4개를 제외한 나머지 오행을 한신으로 반환.
 * 5개 오행 중 최대 4개가 사용되므로 항상 1개 이상 남음.
 */
export function findHanSin(yongSin: Ohaeng, huiSin: Ohaeng, giSin: Ohaeng, guSin: Ohaeng): Ohaeng {
  const used = new Set([yongSin, huiSin, giSin, guSin]);
  return ALL_ELEMENTS.find(e => !used.has(e)) ?? giSin;
}

/**
 * 억부법에 의한 용신 판정.
 *
 * 기본 원리:
 * - 신강/태강/극왕: 설기 필요 → 용신 = 식상/재성/관살
 * - 신약/태약/극약: 부조 필요 → 용신 = 인성/비겁
 * - 중화/중화신강/중화신약: 균형 유지
 *
 * 출력:
 * - yongSin (용신): 가장 필요한 오행
 * - huiSin (희신): 두 번째로 유익한 오행
 * - giSin (기신): 가장 해로운 오행
 * - guSin (구신): 두 번째로 해로운 오행
 * - hanSin (한신): 중립 오행
 */
export function determineYongSin(
  fourPillars: FourPillars,
  strength: DayMasterStrength,
): YongSin {
  const dayMasterElement = getStemElement(fourPillars.day.stem);
  const relation = getElementRelation(dayMasterElement);

  const sameEl = dayMasterElement;               // 비겁
  const generatingDM = relation.generatedBy;     // 인성
  const generatedByDM = relation.generates;      // 식상
  const overcomeByDM = relation.overcomes;       // 재성
  const overcomesDM = relation.overcomeBy;       // 관살

  let yongSin: Ohaeng;
  let huiSin: Ohaeng;
  let giSin: Ohaeng;
  let guSin: Ohaeng;
  let hanSin: Ohaeng;

  const { level } = strength;

  if (level === '극왕' || level === '태강') {
    // Extremely strong: aggressive weakening needed
    yongSin = overcomesDM;   // 관살 - directly weakens
    huiSin = overcomeByDM;   // 재성 - drains energy
    giSin = sameEl;          // 비겁 - strengthens (bad)
    guSin = generatingDM;    // 인성 - strengthens (bad)
    hanSin = generatedByDM;  // 식상 - neutral drain
  } else if (level === '신강') {
    // Strong: moderate weakening
    yongSin = overcomeByDM;  // 재성 - drains energy
    huiSin = generatedByDM;  // 식상 - helps drain
    giSin = generatingDM;    // 인성 - strengthens (bad)
    guSin = sameEl;          // 비겁 - strengthens (bad)
    hanSin = overcomesDM;    // 관살 - too aggressive
  } else if (level === '극약' || level === '태약') {
    // Extremely weak: strong support needed
    yongSin = generatingDM;  // 인성 - generates DM
    huiSin = sameEl;         // 비겁 - same element support
    giSin = overcomesDM;     // 관살 - weakens (bad)
    guSin = overcomeByDM;    // 재성 - drains (bad)
    hanSin = generatedByDM;  // 식상 - drains
  } else if (level === '신약') {
    // Weak: moderate support
    yongSin = sameEl;        // 비겁 - same element support
    huiSin = generatingDM;   // 인성 - generates DM
    giSin = overcomeByDM;    // 재성 - drains (bad)
    guSin = overcomesDM;     // 관살 - weakens (bad)
    hanSin = generatedByDM;  // 식상 - drains
  } else {
    // 중화, 중화신강, 중화신약 - balanced
    yongSin = generatingDM;  // 인성 - gentle support
    huiSin = sameEl;         // 비겁
    giSin = overcomesDM;     // 관살
    guSin = overcomeByDM;    // 재성
    hanSin = generatedByDM;  // 식상
  }

  return { method: '억부', yongSin, huiSin, giSin, guSin, hanSin };
}

/**
 * 신강신약 레벨에 따른 방법론별 적합도 보정값.
 * 중화에 가까울수록 조후/통관 보정↑, 극단적 강약일수록 억부가 명확하므로 조후 보정↓.
 * 억부: 이미 강약 기반으로 계산됨, 병약: 오행 패턴 기반이므로 보정 불필요.
 */
const STRENGTH_MODIFIERS: Partial<Record<YongSinMethod, Partial<Record<StrengthLevel, number>>>> = {
  '조후': {
    '중화': 0.15,
    '중화신강': 0.1,
    '중화신약': 0.1,
    '태강': -0.05,
    '태약': -0.05,
    '극왕': -0.1,
    '극약': -0.1,
  },
  '통관': {
    '중화': 0.1,
    '중화신강': 0.05,
    '중화신약': 0.05,
  },
};

function getStrengthModifier(method: YongSinMethod, level: StrengthLevel): number {
  return STRENGTH_MODIFIERS[method]?.[level] ?? 0;
}

/**
 * 4가지 용신 방법론을 모두 실행하여 적합도 내림차순으로 반환
 */
/** 용신 우선순위에 따른 방법별 가중치 */
const PRIORITY_WEIGHTS: Record<YongshinPriority, Partial<Record<YongSinMethod, number>>> = {
  'hybrid': {},
  'eokbu-first': { '억부': 1.5, '조후': 0.7, '통관': 0.7, '병약': 0.7 },
  'johou-first': { '억부': 0.7, '조후': 1.5, '통관': 0.7, '병약': 0.7 },
};

export function determineAllYongSin(
  fourPillars: FourPillars,
  strength: DayMasterStrength,
  ohaengDistribution: Record<Ohaeng, number>,
  priority?: YongshinPriority,
): YongSinResult[] {
  const yukbu = determineYongSin(fourPillars, strength);
  const yukbuResult: YongSinResult = {
    ...yukbu,
    applicability: calculateYukbuApplicability(strength),
    description: `억부법: ${strength.level}(${strength.score}점) 기준 용신 판정`,
  };

  const johu = determineJohuYongSin(fourPillars);
  const tonggwan = determineTonggwanYongSin(fourPillars, ohaengDistribution);
  const byeongyak = determineByeongyakYongSin(fourPillars, ohaengDistribution, strength);

  const weights = PRIORITY_WEIGHTS[priority ?? 'hybrid'];

  return [yukbuResult, johu, tonggwan, byeongyak]
    .map((result) => {
      const modifier = getStrengthModifier(result.method, strength.level);
      let app = modifier === 0 ? result.applicability : result.applicability + modifier;
      // 우선순위 가중치 적용
      const weight = weights[result.method];
      if (weight) app *= weight;
      return {
        ...result,
        applicability: Math.max(0, Math.min(1, app)),
      };
    })
    .sort((a, b) => b.applicability - a.applicability);
}

function calculateYukbuApplicability(strength: DayMasterStrength): number {
  const { level } = strength;
  switch (level) {
    case '극왕': case '극약': return 0.9;
    case '태강': case '태약': return 0.8;
    case '신강': case '신약': return 0.7;
    case '중화신강': case '중화신약': return 0.5;
    case '중화': return 0.4;
    default: return 0;
  }
}
